import { Autocomplete, Box, Grid, TextField, Typography } from "@mui/material";
import { PropertyMappings } from "./PropertyMappingSelector";
import { useState } from "react";

interface PropertyOption {
	name: string;
	examples: string[];
}

interface GlobalPropertyMappingSelectorProps {
	onApplyMapping: (field: keyof PropertyMappings, value: string | null) => void;
	availableProperties: PropertyOption[];
}

export default function GlobalPropertyMappingSelector({
	onApplyMapping,
	availableProperties,
}: GlobalPropertyMappingSelectorProps) {
	// State to track selected values for each dropdown
	const [selectedValues, setSelectedValues] = useState<Partial<Record<keyof PropertyMappings, PropertyOption | null>>>({
		grower: null,
		farm: null,
		field: null,
		fieldId: null,
	});

	if (availableProperties.length === 0) {
		return null;
	}

	const handleSelectionChange = (field: keyof PropertyMappings, value: PropertyOption | null) => {
		// Update local state to remember the selection
		setSelectedValues((prev) => ({
			...prev,
			[field]: value,
		}));

		// When a selection is made, immediately apply it to all relevant groups
		if (value) {
			onApplyMapping(field, value.name);
		}
		// Don't clear on deselection - just like the type selectors
	};

	const renderGlobalAutocomplete = (field: keyof PropertyMappings, label: string) => {
		const selectedProperty = selectedValues[field];

		return (
			<Box>
				<Autocomplete
					options={availableProperties}
					getOptionLabel={(option) => option.name}
					value={selectedValues[field] || null}
					onChange={(_, value) => handleSelectionChange(field, value)}
					renderOption={(props, option) => (
						<Box component="li" {...props}>
							<Box>
								<Typography variant="body2">{option.name}</Typography>
								{option.examples && option.examples.length > 0 && (
									<Box>
										{option.examples.slice(0, 3).map((example, index) => (
											<Typography key={index} variant="caption" color="text.secondary" sx={{ display: "block" }}>
												{example}
											</Typography>
										))}
									</Box>
								)}
							</Box>
						</Box>
					)}
					renderInput={(params) => (
						<TextField {...params} label={label} size="small" placeholder="Select to apply to all" />
					)}
					clearOnEscape={false}
					isOptionEqualToValue={(option, value) => option.name === value?.name}
				/>
				{selectedProperty && selectedProperty.examples && selectedProperty.examples.length > 0 && (
					<Box sx={{ mt: 0.5, ml: 1.5 }}>
						<Typography variant="caption" color="text.secondary">
							{selectedProperty.examples.slice(0, 3).map((example, idx) => (
								<p key={idx} style={{ fontSize: "0.8em", color: "gray", margin: 0 }}>
									{example}
								</p>
							))}
						</Typography>
					</Box>
				)}
			</Box>
		);
	};

	return (
		<Box sx={{ mb: 3, p: 2, backgroundColor: "background.paper" }}>
			<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
				Property Mappings
			</Typography>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6} lg={3}>
					{renderGlobalAutocomplete("grower", "Grower")}
				</Grid>
				<Grid item xs={12} md={6} lg={3}>
					{renderGlobalAutocomplete("farm", "Farm")}
				</Grid>
				<Grid item xs={12} md={6} lg={3}>
					{renderGlobalAutocomplete("field", "Field")}
				</Grid>
				<Grid item xs={12} md={6} lg={3}>
					{renderGlobalAutocomplete("fieldId", "Field ID")}
				</Grid>
			</Grid>
		</Box>
	);
}
